import React, { useEffect, useState } from "react";
import { ShieldCheck, Plus, Trash, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";
import MerchantLayout from "@/components/MerchantLayout";
import { mApi } from "@/lib/api";

export default function MerchantSecurity() {
  const [keys, setKeys] = useState([]);
  const [selected, setSelected] = useState(null);
  const [newIp, setNewIp] = useState("");
  const [requireHmac, setRequireHmac] = useState(false);
  const [rateLimit, setRateLimit] = useState(600);
  const [whSecret, setWhSecret] = useState(null);

  const load = async () => {
    const r = await mApi.get("/merchant/api-keys");
    const items = r.data.items.filter((k) => !k.revoked);
    setKeys(items);
    if (!selected && items.length) setSelected(items[0]);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (selected) {
      setRequireHmac(!!selected.require_hmac);
      setRateLimit(selected.rate_limit_per_min || 600);
    }
  }, [selected?.id]);

  const addIp = async () => {
    if (!newIp || !selected) return;
    const list = [...(selected.ip_whitelist || []), newIp.trim()];
    const r = await mApi.patch(`/merchant/api-keys/${selected.id}`, { ip_whitelist: list });
    setSelected(r.data); setNewIp("");
    load();
  };
  const removeIp = async (ip) => {
    const list = (selected.ip_whitelist || []).filter((i) => i !== ip);
    const r = await mApi.patch(`/merchant/api-keys/${selected.id}`, { ip_whitelist: list });
    setSelected(r.data); load();
  };
  const saveSecurity = async () => {
    const r = await mApi.patch(`/merchant/api-keys/${selected.id}`, {
      require_hmac: requireHmac, rate_limit_per_min: Number(rateLimit),
    });
    setSelected(r.data); toast.success("Saved"); load();
  };
  const rotateWebhook = async () => {
    const r = await mApi.post("/merchant/webhook-secret/rotate");
    setWhSecret(r.data.webhook_secret);
    toast.success("Webhook secret rotated");
  };

  return (
    <MerchantLayout>
      <div className="p-4 sm:p-6 lg:p-10" data-testid="m-security-page">
        <div className="mb-8">
          <h1 className="font-display text-3xl tracking-tight font-medium">Security</h1>
          <p className="mt-1 text-sm text-zinc-500">IP whitelisting, HMAC signing, rate limits, and webhook secrets.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <aside className="bg-white border border-zinc-200 rounded-sm p-5">
            <div className="text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500 mb-2">Active API keys</div>
            {keys.length === 0 && <p className="text-xs text-zinc-500">Create an API key first.</p>}
            <ul className="space-y-1.5">
              {keys.map((k) => (
                <li key={k.id}>
                  <button
                    onClick={() => setSelected(k)}
                    data-testid={`security-select-${k.id}`}
                    className={`w-full text-left px-3 py-2 rounded-sm text-sm transition-colors duration-150 ${selected?.id === k.id ? "bg-zinc-950 text-white" : "hover:bg-zinc-50"}`}
                  >
                    <div className="font-medium">{k.label || "primary"}</div>
                    <div className={`text-xs font-mono ${selected?.id === k.id ? "text-zinc-300" : "text-zinc-500"}`}>{k.masked}</div>
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          {selected && (
            <div className="lg:col-span-2 space-y-4">
              <div className="bg-white border border-zinc-200 rounded-sm p-5">
                <div className="font-display text-lg tracking-tight font-medium mb-4">IP Whitelist</div>
                <div className="flex gap-2">
                  <input
                    placeholder="e.g. 203.0.113.42"
                    value={newIp} onChange={(e) => setNewIp(e.target.value)}
                    data-testid="security-ip-input"
                    className="flex-1 h-9 px-3 text-sm bg-white border border-zinc-200 rounded-sm focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 outline-none font-mono"
                  />
                  <button onClick={addIp} data-testid="security-add-ip-btn" className="h-9 px-4 text-sm bg-zinc-950 text-white rounded-sm hover:bg-zinc-800 transition-colors duration-150 inline-flex items-center gap-1.5">
                    <Plus size={12} /> Add
                  </button>
                </div>
                <ul className="mt-3 divide-y divide-zinc-100">
                  {(selected.ip_whitelist || []).map((ip) => (
                    <li key={ip} className="py-2 flex items-center justify-between">
                      <span className="font-mono text-xs">{ip}</span>
                      <button onClick={() => removeIp(ip)} className="text-zinc-500 hover:text-red-600 transition-colors duration-150"><Trash size={14} /></button>
                    </li>
                  ))}
                  {(!selected.ip_whitelist || !selected.ip_whitelist.length) && (
                    <li className="py-2 text-xs text-zinc-500">No IPs whitelisted — all source IPs accepted.</li>
                  )}
                </ul>
              </div>

              <div className="bg-white border border-zinc-200 rounded-sm p-5">
                <div className="font-display text-lg tracking-tight font-medium mb-4">Request signing & rate limits</div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={requireHmac} onChange={(e) => setRequireHmac(e.target.checked)} data-testid="security-hmac-toggle" />
                      Require HMAC <code className="text-xs">X-Signature</code>
                    </label>
                    <p className="text-xs text-zinc-500 mt-1">Rejects unsigned writes once enabled. Use the webhook secret to sign requests.</p>
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-700 mb-1.5">Rate limit (req/min)</label>
                    <input
                      type="number" value={rateLimit} onChange={(e) => setRateLimit(e.target.value)}
                      data-testid="security-rate-input"
                      className="w-full h-9 px-3 text-sm bg-white border border-zinc-200 rounded-sm focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 outline-none font-mono"
                    />
                  </div>
                </div>
                <button onClick={saveSecurity} data-testid="security-save-btn" className="mt-4 h-9 px-4 text-sm bg-zinc-950 text-white rounded-sm hover:bg-zinc-800 transition-colors duration-150">
                  Save
                </button>
              </div>

              <div className="bg-white border border-zinc-200 rounded-sm p-5">
                <div className="font-display text-lg tracking-tight font-medium mb-2">Webhook signing secret</div>
                <p className="text-sm text-zinc-600 mb-3">
                  Used to sign outgoing webhooks (and incoming HMAC requests). Rotating invalidates the previous secret immediately.
                </p>
                <button onClick={rotateWebhook} data-testid="rotate-webhook-secret-btn" className="h-9 px-4 text-sm border border-zinc-200 rounded-sm hover:bg-zinc-100 transition-colors duration-150 inline-flex items-center gap-2">
                  <ShieldCheck size={14} /> Rotate webhook secret
                </button>
                {whSecret && (
                  <div className="mt-4 bg-zinc-950 text-emerald-300 font-mono text-xs p-4 rounded-sm break-all">
                    {whSecret}
                    <button
                      onClick={() => { navigator.clipboard.writeText(whSecret); toast.success("Copied"); }}
                      className="block mt-2 text-zinc-300 inline-flex items-center gap-1.5 hover:text-white"
                    >
                      <Copy size={12} /> Copy
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </MerchantLayout>
  );
}
